import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "../../components/AppToast";
import { getallfacility } from "../../services/facility/facility";
import { formatToInputDate } from "../../utils/dateFormatter";
import {
  approveGatePass,
  createGatePass,
  extractGatePassId,
  resolveApprovedByDisplay,
  updateGatePass,
} from "../../services/gatePass/gatePass";
import GatePassItemDetailsForm from "./GatePassItemDetailsForm";
import GatePassItemsTable from "./GatePassItemsTable";
import "./add-stock-transfer-out.css";

const FACILITY_LIST_PAGE_SIZE = 500;

const STATUS_OPTIONS = [
  { value: "", label: "— Select —" },
  { value: "Draft", label: "Draft" },
  { value: "Pending Approval", label: "Pending Approval" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Gate Out Completed", label: "Gate Out Completed" },
];

function strForm(val) {
  if (val == null || val === "") return "";
  return String(val);
}

/** Never show API/serialization placeholders in the Approved By field. */
function displayApproverField(val) {
  const s = strForm(val).trim();
  if (!s) return "";
  if (/^(null|undefined|nan|nil)$/i.test(s)) return "";
  return s;
}

function newLineId() {
  return `gpl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Match facility by Mongo `_id` or by `Facility_ID` (backend expects code, e.g. WH4). */
function resolveWarehouseFromList(warehousesList, selectionKey) {
  const key = strForm(selectionKey);
  if (!key || !Array.isArray(warehousesList)) return null;
  return (
    warehousesList.find((f) => f._id === key) ||
    warehousesList.find((f) => strForm(f.Facility_ID) === key) ||
    null
  );
}

/** Display / audit label for the logged-in user (Redux `login.userDetail` shapes vary by tenant). */
function resolveUserLabel(userDetail) {
  if (!userDetail || typeof userDetail !== "object") return "";
  const fn = strForm(userDetail.firstName);
  const ln = strForm(userDetail.lastName);
  const fullFromParts = [fn, ln].filter(Boolean).join(" ").trim();
  return (
    fullFromParts ||
    strForm(userDetail.fullName) ||
    strForm(userDetail.displayName) ||
    strForm(userDetail.userName) ||
    strForm(userDetail.username) ||
    strForm(userDetail.name) ||
    strForm(userDetail.email) ||
    strForm(userDetail.userId?.email) ||
    (typeof userDetail.userId === "string" ? strForm(userDetail.userId) : "") ||
    ""
  );
}

function mapSavedItemsToLines(items) {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((it, idx) => ({
    id: strForm(it._id || it.id || `saved-${idx}`),
    gate_pass_id: strForm(it.gate_pass_id),
    sku_code: strForm(it.sku_code),
    item_name: strForm(it.item_name),
    quantity: it.quantity != null && it.quantity !== "" ? String(it.quantity) : "",
    uom: strForm(it.uom),
    batch_no: strForm(it.batch_no ?? it.batchNo),
    serial_no: strForm(it.serial_no ?? it.serialNo),
    item_condition: strForm(it.item_condition ?? it.itemCondition),
    asset_tag: strForm(it.asset_tag ?? it.assetTag),
    remarks: strForm(it.remarks),
  }));
}

function isTerminalGatePassStatus(st) {
  const s = strForm(st).trim().toLowerCase();
  return s === "approved" || s === "rejected" || s === "gate_out_completed";
}

export default function GatePass({
  onCancel,
  onSuccess,
  onApproved,
  viewOnly = false,
  initialDocument = null,
}) {
  const userDetail = useSelector((state) => state?.login?.userDetail);
  const createdByLabel = useMemo(() => resolveUserLabel(userDetail), [userDetail]);

  const [warehousesLoading, setWarehousesLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);

  const [gate_pass_no, setGate_pass_no] = useState("");
  const [gate_pass_date, setGate_pass_date] = useState(
    () => formatToInputDate(new Date(), false) || ""
  );
  const [warehouse_id, setWarehouse_id] = useState("");
  const [requester_id, setRequester_id] = useState("");
  const [approved_by, setApproved_by] = useState("");
  const [vendor_name, setVendor_name] = useState("");
  const [vehicle_no, setVehicle_no] = useState("");
  const [driver_name, setDriver_name] = useState("");
  const [purpose, setPurpose] = useState("");
  const [status, setStatus] = useState("Pending Approval");
  const [document_name, setDocument_name] = useState("");
  const [uploaded_by, setUploaded_by] = useState("");
  const [uploaded_at, setUploaded_at] = useState("");
  const [remarks, setRemarks] = useState("");

  const [items, setItems] = useState([]);
  const [editItemId, setEditItemId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  const fieldLocked = viewOnly;

  const isEditingRecord = Boolean(initialDocument) && !viewOnly;

  const showApproveAction = useMemo(() => {
    if (!viewOnly || !initialDocument) return false;
    if (!extractGatePassId(initialDocument)) return false;
    if (isTerminalGatePassStatus(status)) return false;
    if (displayApproverField(approved_by)) return false;
    return true;
  }, [viewOnly, initialDocument, status, approved_by]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWarehousesLoading(true);
      try {
        const res = await getallfacility(0, FACILITY_LIST_PAGE_SIZE);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (!cancelled) setWarehouses(list);
      } catch (err) {
        if (!cancelled) {
          console.error("facility/fetch failed", err);
          toast.error(typeof err === "string" ? err : "Failed to load warehouses");
          setWarehouses([]);
        }
      } finally {
        if (!cancelled) setWarehousesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initialDocument) return;
    const doc = initialDocument;
    setGate_pass_no(strForm(doc.gate_pass_no ?? doc.gatePassNo));
    setGate_pass_date(
      formatToInputDate(doc.gate_pass_date ?? doc.gatePassDate, false) ||
        (typeof doc.gate_pass_date === "string" ? doc.gate_pass_date.slice(0, 10) : "") ||
        ""
    );
    const wid = strForm(doc.warehouse_id);
    const match = resolveWarehouseFromList(warehouses, wid);
    setWarehouse_id(match ? match._id : wid);
    setRequester_id(strForm(doc.requester_id));
    setApproved_by(displayApproverField(resolveApprovedByDisplay(doc)));
    setVendor_name(strForm(doc.vendor_name));
    setVehicle_no(strForm(doc.vehicle_no));
    setDriver_name(strForm(doc.driver_name));
    setPurpose(strForm(doc.purpose));
    setStatus(strForm(doc.status) || "Pending Approval");
    setDocument_name(strForm(doc.document_name));
    setUploaded_by(strForm(doc.uploaded_by));
    setUploaded_at(strForm(doc.uploaded_at));
    setRemarks(strForm(doc.remarks));
    setItems(mapSavedItemsToLines(doc.items));
  }, [initialDocument, warehouses]);

  const warehouseLabel = useMemo(() => {
    const w = resolveWarehouseFromList(warehouses, warehouse_id);
    return w ? strForm(w.Facility_Name || w.Facility_ID) : "";
  }, [warehouses, warehouse_id]);

  const editLineInitialValues = useMemo(() => {
    if (!editItemId) return {};
    const row = items.find((r) => r.id === editItemId);
    if (!row) return {};
    return {
      gate_pass_id: row.gate_pass_id || "",
      sku_code: row.sku_code,
      item_name: row.item_name,
      quantity: row.quantity,
      uom: row.uom,
      batch_no: row.batch_no,
      serial_no: row.serial_no,
      item_condition: row.item_condition,
      asset_tag: row.asset_tag,
      remarks: row.remarks,
    };
  }, [editItemId, items]);

  const handleAddLineFromForm = (payload) => {
    if (editItemId) {
      setItems((prev) =>
        prev.map((row) =>
          row.id === editItemId ? { ...row, ...payload, id: row.id } : row
        )
      );
      setEditItemId(null);
      toast.success("Line updated.");
    } else {
      setItems((prev) => [...prev, { id: newLineId(), ...payload }]);
      toast.success("Line added.");
    }
  };

  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    if (editItemId === itemId) setEditItemId(null);
  };

  const mapLinesToApiItems = (lines) =>
    (Array.isArray(lines) ? lines : []).map(({ id: _id, gate_pass_id: _gp, ...rest }) => ({
      sku_code: String(rest.sku_code || "").trim(),
      item_name: String(rest.item_name || "").trim(),
      quantity: Number(rest.quantity) || 0,
      uom: String(rest.uom || "").trim(),
      batchNo: String(rest.batch_no ?? rest.batchNo ?? "").trim(),
      serialNo: String(rest.serial_no ?? rest.serialNo ?? "").trim(),
      itemCondition: String(rest.item_condition ?? rest.itemCondition ?? "").trim(),
      assetTag: String(rest.asset_tag ?? rest.assetTag ?? "").trim(),
      remarks: String(rest.remarks ?? "").trim(),
    }));

  const buildGatePassApiPayload = (overrides = {}) => {
    const wh = resolveWarehouseFromList(warehouses, warehouse_id);
    const gateNoFinal =
      gate_pass_no.trim() ||
      strForm(initialDocument?.gate_pass_no ?? initialDocument?.gatePassNo) ||
      `GP-${Date.now()}`;

    const linesForApi =
      items.length > 0
        ? items
        : mapSavedItemsToLines(initialDocument?.items);
    const lineItemsForApi = mapLinesToApiItems(linesForApi);

    const warehouseForApi = strForm(
      wh?.Facility_ID ||
        wh?.Facility_Code ||
        wh?.warehouseCode ||
        warehouse_id ||
        initialDocument?.warehouse_id
    );
    const warehouseLabelForApi = strForm(
      wh?.Facility_Name ||
        wh?.Facility_ID ||
        initialDocument?.warehouse_label ||
        ""
    );

    return {
      gate_pass_no: gateNoFinal,
      gate_pass_date,
      warehouse_id: warehouseForApi,
      warehouse_label: warehouseLabelForApi,
      requester_id: requester_id.trim(),
      vendor_name: vendor_name.trim(),
      vehicle_no: vehicle_no.trim(),
      driver_name: driver_name.trim(),
      purpose: purpose.trim(),
      status: status || "Pending Approval",
      document_name: document_name.trim(),
      uploaded_by: uploaded_by.trim(),
      uploaded_at: uploaded_at.trim(),
      approved_by: approved_by.trim(),
      remarks: remarks.trim(),
      items: lineItemsForApi,
      ...overrides,
    };
  };

  const handleSave = async () => {
    if (viewOnly) return;
    if (!gate_pass_date) {
      toast.error("Please enter gate pass date.");
      return;
    }
    if (!warehouse_id) {
      toast.error("Please select warehouse.");
      return;
    }
    if (items.length === 0) {
      toast.error("Add at least one item line.");
      return;
    }

    const gateNoFinal = gate_pass_no.trim() || `GP-${Date.now()}`;

    const isEdit = Boolean(initialDocument) && !viewOnly;
    const editId = isEdit ? extractGatePassId(initialDocument) : "";

    const payload = buildGatePassApiPayload({
      gate_pass_no: gateNoFinal,
    });

    setSaving(true);
    try {
      const saved = isEdit && editId
        ? await updateGatePass(editId, payload)
        : await createGatePass(payload);
      toast.success(isEdit ? "Gate pass updated successfully" : "Gate pass saved successfully");
      if (typeof onSuccess === "function") {
        onSuccess(saved);
      } else {
        onCancel?.();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to save gate pass");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!viewOnly || !initialDocument) return;
    const id = extractGatePassId(initialDocument);
    if (!id) {
      toast.error("Cannot approve: missing gate pass id.");
      return;
    }
    const approver = createdByLabel.trim();
    if (!approver) {
      toast.error("Cannot resolve current user for approval.");
      return;
    }
    setApproving(true);
    try {
      const payload = buildGatePassApiPayload({
        approved_by: approver,
        approvedBy: approver,
        status: "Approved",
      });
      const updated = await approveGatePass(id, {
        approved_by: approver,
        document: payload,
      });
      const resolved = displayApproverField(resolveApprovedByDisplay(updated)) || approver;
      const docForParent = {
        ...initialDocument,
        ...updated,
        approved_by: resolved,
        approvedBy: resolved,
        status: strForm(updated.status ?? updated.approvalStatus) || "Approved",
      };
      setApproved_by(resolved);
      setStatus(docForParent.status);
      onApproved?.(docForParent);
      toast.success("Gate pass approved.");
    } catch (err) {
      toast.error(err?.message || "Failed to approve gate pass");
    } finally {
      setApproving(false);
    }
  };

  const formatTs = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return strForm(iso);
      return d.toLocaleString();
    } catch {
      return strForm(iso);
    }
  };

  return (
    <div className="sto-create-root">
      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-info-circle" aria-hidden />
          Basic Information
        </div>
        <div className="sto-section-body">
          <div className="row g-3">
            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-gate_pass_no">
                Gate Pass No
              </label>
              <input
                id="gp-gate_pass_no"
                type="text"
                className="form-control"
                value={gate_pass_no}
                onChange={(e) => setGate_pass_no(e.target.value)}
                placeholder="Optional — auto-generated if empty"
                disabled={fieldLocked}
              />
            </div>
            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-gate_pass_date">
                Gate Pass Date
              </label>
              <input
                id="gp-gate_pass_date"
                type="date"
                className="form-control"
                value={gate_pass_date}
                onChange={(e) => setGate_pass_date(e.target.value)}
                disabled={fieldLocked}
              />
            </div>
            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-warehouse_id">
                Warehouse ID
              </label>
              {viewOnly ? (
                <input
                  id="gp-warehouse_id"
                  type="text"
                  className="form-control"
                  readOnly
                  value={
                    initialDocument?.warehouse_label ||
                    warehouseLabel ||
                    warehouse_id ||
                    "—"
                  }
                />
              ) : (
                <select
                  id="gp-warehouse_id"
                  className="form-select"
                  value={warehousesLoading ? "" : warehouse_id}
                  onChange={(e) => setWarehouse_id(e.target.value)}
                  disabled={fieldLocked || warehousesLoading || warehouses.length === 0}
                >
                  {warehousesLoading ? (
                    <option value="">Loading warehouses…</option>
                  ) : warehouses.length === 0 ? (
                    <option value="">No warehouses available</option>
                  ) : (
                    <>
                      <option value="">— Select —</option>
                      {warehouses.map((w) => (
                        <option key={w._id} value={w._id}>
                          {w.Facility_Name || w.Facility_ID || w._id}
                          {w.Facility_ID ? ` (${w.Facility_ID})` : ""}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              )}
            </div>

            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-requester_id">
                Requester ID
              </label>
              <input
                id="gp-requester_id"
                type="text"
                className="form-control"
                value={requester_id}
                onChange={(e) => setRequester_id(e.target.value)}
                disabled={fieldLocked}
              />
            </div>
            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-vendor_name">
                Vendor Name
              </label>
              <input
                id="gp-vendor_name"
                type="text"
                className="form-control"
                value={vendor_name}
                onChange={(e) => setVendor_name(e.target.value)}
                disabled={fieldLocked}
              />
            </div>

            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-vehicle_no">
                Vehicle No
              </label>
              <input
                id="gp-vehicle_no"
                type="text"
                className="form-control"
                value={vehicle_no}
                onChange={(e) => setVehicle_no(e.target.value)}
                disabled={fieldLocked}
              />
            </div>
            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-driver_name">
                Driver Name
              </label>
              <input
                id="gp-driver_name"
                type="text"
                className="form-control"
                value={driver_name}
                onChange={(e) => setDriver_name(e.target.value)}
                disabled={fieldLocked}
              />
            </div>
            <div className="col-md-4 col-sm-12">
              <label className="form-label" htmlFor="gp-purpose">
                Purpose
              </label>
              <input
                id="gp-purpose"
                type="text"
                className="form-control"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                disabled={fieldLocked}
              />
            </div>

            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-status">
                Status
              </label>
              <select
                id="gp-status"
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={fieldLocked}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value || "empty"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 col-sm-12">
              <label className="form-label" htmlFor="gp-document_name">
                Document Name (file name)
              </label>
              {viewOnly ? (
                <input
                  id="gp-document_name"
                  type="text"
                  className="form-control"
                  readOnly
                  value={document_name || "—"}
                />
              ) : (
                <>
                  <input
                    id="gp-document_name"
                    type="file"
                    className="form-control"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setDocument_name(f.name);
                        setUploaded_by(createdByLabel || "");
                        setUploaded_at(new Date().toISOString());
                      } else {
                        setDocument_name("");
                        setUploaded_by("");
                        setUploaded_at("");
                      }
                    }}
                  />
                  {document_name ? (
                    <div className="form-text">{document_name}</div>
                  ) : null}
                </>
              )}
            </div>

            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-approved_by">
                Approved By
              </label>
              <input
                id="gp-approved_by"
                type="text"
                className="form-control"
                readOnly
                value={
                  displayApproverField(approved_by)
                    ? displayApproverField(approved_by)
                    : viewOnly
                      ? "—"
                      : ""
                }
                disabled={fieldLocked}
              />
            </div>

            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-uploaded_by">
                Uploaded By
              </label>
              <input
                id="gp-uploaded_by"
                type="text"
                className="form-control"
                readOnly
                value={uploaded_by || "—"}
              />
            </div>
            <div className="col-md-4 col-sm-6">
              <label className="form-label" htmlFor="gp-uploaded_at">
                Uploaded At
              </label>
              <input
                id="gp-uploaded_at"
                type="text"
                className="form-control"
                readOnly
                value={uploaded_at ? formatTs(uploaded_at) : "—"}
              />
            </div>

            <div className="col-12">
              <label className="form-label" htmlFor="gp-remarks">
                Remarks
              </label>
              <textarea
                id="gp-remarks"
                className="form-control"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={fieldLocked}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-list-ul" aria-hidden />
          Item Details
        </div>
        <div className="sto-section-body">
          {!viewOnly && (
            <>
              <div className="sto-items-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 className="mb-0">
                  <i className="bi bi-box-seam text-primary" aria-hidden />
                  Line Items
                  <span className="badge bg-light text-dark border fw-normal ms-1">{items.length}</span>
                </h6>
              </div>
              <GatePassItemDetailsForm
                key={editItemId || "new-line"}
                initialValues={editLineInitialValues}
                isEditMode={Boolean(editItemId)}
                readOnly={false}
                onAddToTable={handleAddLineFromForm}
                onCancel={() => setEditItemId(null)}
              />
            </>
          )}

          <GatePassItemsTable
            items={items}
            onEditItem={viewOnly ? undefined : (row) => setEditItemId(row.id)}
            onDeleteItem={viewOnly ? undefined : removeItem}
            readOnly={viewOnly}
          />

          {items.length === 0 && (
            <div className="alert alert-light border mb-0 small mt-3">
              {viewOnly ? "No item lines." : "No line items yet."}
            </div>
          )}

          {showApproveAction && (
            <div
              className="d-flex justify-content-center py-4 mt-3 border-top"
              role="region"
              aria-label="Gate pass approval"
            >
              <button
                type="button"
                className="btn btn-success btn-lg px-5 shadow-sm"
                onClick={handleApprove}
                disabled={approving}
              >
                <i className="bi bi-check2-circle me-2" aria-hidden />
                {approving ? "Approving…" : "Approve"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="sto-footer-actions d-flex justify-content-end gap-2 mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => onCancel?.()}
          disabled={saving || approving}
        >
          {viewOnly ? "Back" : "Cancel"}
        </button>
        {!viewOnly && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || warehousesLoading}
          >
            <i className="bi bi-check2-circle me-1" aria-hidden />
            {saving ? "Saving…" : isEditingRecord ? "Save changes" : "Save gate pass"}
          </button>
        )}
      </div>
    </div>
  );
}
